import {
  MEDIA_ACCEPTED_TYPES,
  mediaVariant,
  parseEmbedUrl,
  safeHref,
  type RichDoc,
} from '@growup/site-shared';
import { cx } from '@growup/ui';
import { mergeAttributes, Node, type JSONContent } from '@tiptap/core';
import Image from '@tiptap/extension-image';
import { EditorContent, useEditor, useEditorState, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useId, useRef, useState, type ReactNode } from 'react';

import { messageOf, uploadImage } from '../api';
import { checkImageFile } from '../media/ImageUpload';

/**
 * Editor de texto rico das noticias e das paginas (docs/conteudos), feito com
 * o TipTap. So oferece o que o site sabe mostrar (rich-text.ts em
 * site-shared): titulos, negrito, italico, ligacoes, listas, citacoes, linha,
 * imagens carregadas e videos do YouTube ou do Twitch. O que se cola de outro
 * sitio (Word, um site) perde o resto da formatacao.
 */

/** Video do YouTube ou do Twitch: um bloco que o site mostra com o leitor. */
const Embed = Node.create({
  name: 'embed',
  group: 'block',
  atom: true,
  draggable: true,
  addAttributes() {
    return {
      provider: { default: 'youtube' },
      id: { default: '' },
    };
  },
  parseHTML() {
    return [
      {
        tag: 'div[data-embed]',
        getAttrs: (el) => ({
          provider: el.getAttribute('data-embed'),
          id: el.getAttribute('data-id'),
        }),
      },
    ];
  },
  renderHTML({ HTMLAttributes, node }) {
    const provider = String(node.attrs.provider) === 'twitch' ? 'Twitch' : 'YouTube';
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-embed': node.attrs.provider as string,
        'data-id': node.attrs.id as string,
        class: 'gu-embed',
      }),
      `▶ Vídeo do ${provider}: ${String(node.attrs.id)}`,
    ];
  },
});

export const EDITOR_EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [2, 3] },
    code: false,
    codeBlock: false,
    strike: false,
    underline: false,
    link: {
      openOnClick: false,
      autolink: true,
      defaultProtocol: 'https',
      isAllowedUri: (url) => safeHref(url) !== null,
      HTMLAttributes: { rel: 'noopener noreferrer', target: null },
    },
  }),
  Image.configure({ inline: false, allowBase64: false }),
  Embed,
];

/** Classes do texto rico, as mesmas ideias do site, no tema do back-office. */
export const RICH_TEXT_CLASSES =
  'flex flex-col gap-3 text-body [&_a]:text-gu-accent [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-gu-line [&_blockquote]:pl-4 [&_blockquote]:italic [&_h2]:mt-4 [&_h2]:font-display [&_h2]:text-headline [&_h2]:uppercase [&_h3]:mt-2 [&_h3]:font-display [&_h3]:text-title [&_h3]:uppercase [&_hr]:border-gu-line [&_img]:max-h-80 [&_img]:w-auto [&_ol]:ml-6 [&_ol]:list-decimal [&_ul]:ml-6 [&_ul]:list-disc [&_.gu-embed]:border-2 [&_.gu-embed]:border-dashed [&_.gu-embed]:border-gu-line [&_.gu-embed]:px-4 [&_.gu-embed]:py-6 [&_.gu-embed]:text-center [&_.gu-embed]:text-gu-ink-muted [&_.ProseMirror-selectednode]:outline [&_.ProseMirror-selectednode]:outline-2 [&_.ProseMirror-selectednode]:outline-gu-accent';

function ToolButton({
  label,
  active,
  disabled,
  onClick,
  children,
}: {
  label: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      aria-pressed={active}
      disabled={disabled}
      // Sem roubar o foco ao texto: a selecao fica onde estava.
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className={cx(
        'min-w-9 border-2 px-2 py-1 font-sub text-label font-bold disabled:opacity-40',
        active
          ? 'border-gu-accent bg-gu-accent text-gu-on-accent'
          : 'border-gu-line hover:border-gu-ink',
      )}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor, onProblem }: { editor: Editor; onProblem: (m: string | null) => void }) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      h2: e.isActive('heading', { level: 2 }),
      h3: e.isActive('heading', { level: 3 }),
      bullet: e.isActive('bulletList'),
      ordered: e.isActive('orderedList'),
      quote: e.isActive('blockquote'),
      link: e.isActive('link'),
      image: e.isActive('image'),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  });
  const chain = () => editor.chain().focus();

  const setLink = () => {
    const current = editor.getAttributes('link').href as string | undefined;
    const raw = window.prompt('Endereço da ligação (vazio para tirar)', current ?? 'https://');
    if (raw === null) return;
    if (raw.trim() === '' || raw.trim() === 'https://') {
      chain().extendMarkRange('link').unsetLink().run();
      return;
    }
    const href = safeHref(raw);
    if (href === null) {
      onProblem('Ligação inválida: use https://..., mailto:... ou um caminho do site (/loja).');
      return;
    }
    onProblem(null);
    chain().extendMarkRange('link').setLink({ href }).run();
  };

  const addVideo = () => {
    const raw = window.prompt('Link do vídeo (YouTube ou Twitch)');
    if (raw === null || raw.trim() === '') return;
    const embed = parseEmbedUrl(raw);
    if (embed === null) {
      onProblem('Só links de vídeos do YouTube ou do Twitch (vídeo, clip ou canal).');
      return;
    }
    onProblem(null);
    chain().insertContent({ type: 'embed', attrs: embed }).run();
  };

  const addImage = async (file: File) => {
    const problem = checkImageFile(file);
    if (problem) {
      onProblem(problem);
      return;
    }
    setBusy(true);
    try {
      const image = await uploadImage(file);
      const alt = window.prompt(
        'Descreva a imagem numa frase (para quem não a vê e para os motores de busca)',
        '',
      );
      onProblem(alt?.trim() ? null : 'Falta a descrição da imagem: selecione-a e use «Descrição».');
      chain()
        .setImage({ src: mediaVariant(image.url, 'lg'), alt: alt?.trim() ?? '' })
        .run();
    } catch (e) {
      onProblem(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const editAlt = () => {
    const current = (editor.getAttributes('image').alt as string | undefined) ?? '';
    const alt = window.prompt('Descrição da imagem', current);
    if (alt === null) return;
    chain().updateAttributes('image', { alt: alt.trim() }).run();
  };

  return (
    <div
      className="flex flex-wrap gap-1 border-b-2 border-gu-line p-2"
      role="toolbar"
      aria-label="Formatação"
    >
      <ToolButton label="Negrito" active={state.bold} onClick={() => chain().toggleBold().run()}>
        B
      </ToolButton>
      <ToolButton
        label="Itálico"
        active={state.italic}
        onClick={() => chain().toggleItalic().run()}
      >
        <span className="italic">I</span>
      </ToolButton>
      <ToolButton
        label="Título"
        active={state.h2}
        onClick={() => chain().toggleHeading({ level: 2 }).run()}
      >
        T1
      </ToolButton>
      <ToolButton
        label="Subtítulo"
        active={state.h3}
        onClick={() => chain().toggleHeading({ level: 3 }).run()}
      >
        T2
      </ToolButton>
      <ToolButton
        label="Lista"
        active={state.bullet}
        onClick={() => chain().toggleBulletList().run()}
      >
        •
      </ToolButton>
      <ToolButton
        label="Lista numerada"
        active={state.ordered}
        onClick={() => chain().toggleOrderedList().run()}
      >
        1.
      </ToolButton>
      <ToolButton
        label="Citação"
        active={state.quote}
        onClick={() => chain().toggleBlockquote().run()}
      >
        ❝
      </ToolButton>
      <ToolButton label="Linha divisória" onClick={() => chain().setHorizontalRule().run()}>
        —
      </ToolButton>
      <ToolButton label="Ligação" active={state.link} onClick={setLink}>
        Ligação
      </ToolButton>
      <ToolButton label="Imagem" disabled={busy} onClick={() => fileInput.current?.click()}>
        {busy ? 'A carregar…' : 'Imagem'}
      </ToolButton>
      {state.image ? (
        <ToolButton label="Descrição da imagem" onClick={editAlt}>
          Descrição
        </ToolButton>
      ) : null}
      <ToolButton label="Vídeo" onClick={addVideo}>
        Vídeo
      </ToolButton>
      <span className="mx-1 border-l-2 border-gu-line" aria-hidden="true" />
      <ToolButton label="Desfazer" disabled={!state.canUndo} onClick={() => chain().undo().run()}>
        ↶
      </ToolButton>
      <ToolButton label="Refazer" disabled={!state.canRedo} onClick={() => chain().redo().run()}>
        ↷
      </ToolButton>
      <input
        ref={fileInput}
        type="file"
        accept={MEDIA_ACCEPTED_TYPES.join(',')}
        hidden
        data-testid="editor-image-input"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void addImage(file);
        }}
      />
    </div>
  );
}

/** O texto rico. `value` so e lido ao montar; o editor guarda o seu proprio estado. */
export function RichEditor({
  label,
  value,
  onChange,
  error,
  hint,
}: {
  label: string;
  value: RichDoc;
  onChange: (doc: RichDoc) => void;
  error?: string | undefined;
  hint?: ReactNode;
}) {
  const labelId = useId();
  const [problem, setProblem] = useState<string | null>(null);
  const editor = useEditor({
    extensions: EDITOR_EXTENSIONS,
    content: value as JSONContent,
    immediatelyRender: true,
    editorProps: {
      attributes: {
        'aria-labelledby': labelId,
        'aria-multiline': 'true',
        role: 'textbox',
        class: 'min-h-64 px-4 py-3 focus:outline-none',
      },
    },
    onUpdate: ({ editor: e }) => onChange(e.getJSON() as RichDoc),
  });

  return (
    <div className="flex flex-col gap-1.5">
      <span id={labelId} className="font-sub text-label font-bold uppercase text-gu-ink-muted">
        {label}
      </span>
      <div
        className={cx(
          'border-2 bg-gu-sunken focus-within:border-gu-focus',
          error ? 'border-gu-danger' : 'border-gu-line',
        )}
      >
        {editor ? <Toolbar editor={editor} onProblem={setProblem} /> : null}
        <EditorContent editor={editor} className={RICH_TEXT_CLASSES} />
      </div>
      {hint ? <span className="text-caption text-gu-ink-muted">{hint}</span> : null}
      {(problem ?? error) ? (
        <span role="alert" className="text-caption font-semibold text-gu-danger">
          <span aria-hidden="true">✕ </span>
          {problem ?? error}
        </span>
      ) : null}
    </div>
  );
}
